import { CalendarDays } from 'lucide-react';
import { type ComponentProps, type ReactNode, useState } from 'react';

import { InputGroupButton } from '@/_shared/components/ui/input-group';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { cn } from '@/_shared/lib/utils';

/** The shared chrome behind DateValue + DateRangeValue: an InputGroup chip (calendar glyph + a label that
 *  goes muted when unset) opening a Popover calendar. Owns the open/close state and exposes `close` to the
 *  `children` render prop so each value control supplies just its own `<Calendar>` (mode + onSelect). */
export function CalendarPopover({
  ariaLabel,
  hasValue,
  label,
  triggerVariant,
  triggerClassName,
  children,
}: {
  ariaLabel: string;
  hasValue: boolean;
  label: ReactNode;
  triggerVariant?: ComponentProps<typeof InputGroupButton>['variant'];
  triggerClassName?: string;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <InputGroupButton
          variant={triggerVariant}
          aria-label={ariaLabel}
          Icon={<CalendarDays className="size-3.5" />}
          className={cn('font-medium', hasValue ? 'text-foreground' : 'text-muted-foreground', triggerClassName)}
        >
          {label}
        </InputGroupButton>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        {children(() => setOpen(false))}
      </PopoverContent>
    </Popover>
  );
}
